import config from "../../../config";
import { getlist, post } from "../../../contexts/apicontexts";
import { getAllTaskList, normalizeEmployee } from "../taskService";
import {
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
  normalizeTaskPriority,
  normalizeTaskStatus,
} from "../taskUtils";

const normalizeId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizeString = (value) => String(value ?? "").trim();

const normalizeDepartment = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  name: normalizeString(item?.name ?? item?.Name),
  code: normalizeString(item?.code ?? item?.Code),
});

const normalizeReportEmployee = (item) => {
  const employee = normalizeEmployee(item);

  return {
    ...employee,
    departmentId: Number(item?.departmentId ?? item?.DepartmentId ?? item?.departmentID ?? item?.DepartmentID ?? 0),
    departmentName: normalizeString(item?.departmentName ?? item?.DepartmentName),
    designationName: normalizeString(item?.designationName ?? item?.DesignationName),
  };
};

const normalizeTaskReportItem = (item) => ({
  taskId: Number(item?.taskId ?? item?.TaskId ?? item?.id ?? item?.Id ?? 0),
  ticketId: Number(item?.ticketId ?? item?.TicketId ?? 0),
  ticketNumber: normalizeString(item?.ticketNumber ?? item?.TicketNumber),
  ticketSubject: normalizeString(item?.ticketSubject ?? item?.TicketSubject),
  title: normalizeString(item?.title ?? item?.Title) || "Untitled task",
  description: normalizeString(item?.description ?? item?.Description),
  status: normalizeTaskStatus(item?.status ?? item?.Status),
  priority: normalizeTaskPriority(item?.priority ?? item?.Priority),
  progressPercent: Number(item?.progressPercent ?? item?.ProgressPercent ?? 0),
  dueDate: item?.dueDate ?? item?.DueDate ?? "",
  assignedEmployeeId: Number(item?.assignedEmployeeId ?? item?.AssignedEmployeeId ?? 0),
  assignedUserId: normalizeString(item?.assignedUserId ?? item?.AssignedUserId),
  assignedEmployeeName: normalizeString(item?.assignedEmployeeName ?? item?.AssignedEmployeeName) || "Unassigned",
  assignedEmployeeEmail: normalizeString(item?.assignedEmployeeEmail ?? item?.AssignedEmployeeEmail),
  departmentId: Number(item?.departmentId ?? item?.DepartmentId ?? 0),
  departmentName: normalizeString(item?.departmentName ?? item?.DepartmentName) || "No Department",
  createdByUserId: normalizeString(item?.createdByUserId ?? item?.CreatedByUserId),
  createdByName: normalizeString(item?.createdByName ?? item?.CreatedByName),
  createdAt: item?.createdAt ?? item?.CreatedAt ?? "",
  updatedAt: item?.updatedAt ?? item?.UpdatedAt ?? "",
  lastActivityAt: item?.lastActivityAt ?? item?.LastActivityAt ?? item?.updatedAt ?? item?.UpdatedAt ?? "",
  lastActionLabel: normalizeString(item?.lastActionLabel ?? item?.LastActionLabel) || "Task Updated",
  lastActorName: normalizeString(item?.lastActorName ?? item?.LastActorName),
  lastNote: normalizeString(item?.lastNote ?? item?.LastNote),
});

const normalizeTaskReportStatusCount = (item) => ({
  status: normalizeTaskStatus(item?.status ?? item?.Status),
  count: Number(item?.count ?? item?.Count ?? 0),
});

const normalizeTaskReportPriorityCount = (item) => ({
  priority: normalizeTaskPriority(item?.priority ?? item?.Priority),
  count: Number(item?.count ?? item?.Count ?? 0),
});

const normalizeTaskReportResponse = (response) => ({
  items: Array.isArray(response?.items ?? response?.Items)
    ? (response?.items ?? response?.Items).map(normalizeTaskReportItem)
    : [],
  statusCounts: Array.isArray(response?.statusCounts ?? response?.StatusCounts)
    ? (response?.statusCounts ?? response?.StatusCounts).map(normalizeTaskReportStatusCount)
    : [],
  priorityCounts: Array.isArray(response?.priorityCounts ?? response?.PriorityCounts)
    ? (response?.priorityCounts ?? response?.PriorityCounts).map(normalizeTaskReportPriorityCount)
    : [],
  totalTasks: Number(response?.totalTasks ?? response?.TotalTasks ?? 0),
});

const buildCounts = (items, key, options, normalizer) =>
  options.map((option) => ({
    [key]: option,
    count: items.filter((item) => normalizer(item[key]) === option).length,
  }));

const isWithinDateRange = (value, startDate, endDate) => {
  if (!value) return false;

  const current = new Date(value);
  if (Number.isNaN(current.getTime())) return false;

  const currentDate = current.toISOString().slice(0, 10);
  return currentDate >= startDate && currentDate <= endDate;
};

const applyLocalFilters = (items, filters, employees) => {
  const employeeMapById = new Map(employees.map((item) => [String(item.id || ""), item]));
  const employeeMapByUserId = new Map(employees.map((item) => [String(item.userId || ""), item]));

  const enrichedItems = items.map((item) => {
    const employee =
      employeeMapById.get(String(item.assignedEmployeeId || "")) ||
      employeeMapByUserId.get(String(item.assignedUserId || "")) ||
      null;

    return normalizeTaskReportItem({
      ...item,
      departmentId: item.departmentId || employee?.departmentId || 0,
      departmentName: item.departmentName || employee?.departmentName || "No Department",
      lastActivityAt: item.lastActivityAt || item.updatedAt || item.createdAt || "",
    });
  });

  const filteredItems = enrichedItems.filter((item) => {
    const matchesDepartment = filters.departmentId
      ? Number(item.departmentId || 0) === Number(filters.departmentId)
      : true;
    const matchesEmployee = filters.assignedEmployeeId
      ? Number(item.assignedEmployeeId || 0) === Number(filters.assignedEmployeeId)
      : true;
    const matchesStatus = filters.status
      ? normalizeTaskStatus(item.status) === normalizeTaskStatus(filters.status)
      : true;
    const matchesPriority = filters.priority
      ? normalizeTaskPriority(item.priority) === normalizeTaskPriority(filters.priority)
      : true;
    const matchesDate =
      filters.startDate && filters.endDate
        ? isWithinDateRange(item.lastActivityAt || item.updatedAt || item.createdAt, filters.startDate, filters.endDate)
        : true;

    return matchesDepartment && matchesEmployee && matchesStatus && matchesPriority && matchesDate;
  });

  return {
    items: filteredItems,
    totalTasks: filteredItems.length,
    statusCounts: buildCounts(filteredItems, "status", TASK_STATUS_OPTIONS, normalizeTaskStatus),
    priorityCounts: buildCounts(filteredItems, "priority", TASK_PRIORITY_OPTIONS, normalizeTaskPriority),
  };
};

export const fetchAllTaskReportFilterOptions = async () => {
  try {
    const [departmentResponse, employeeResponse] = await Promise.all([
      getlist(`${config.baseurl}Departments/getlist`),
      getlist(`${config.baseurl}Employee/getlist`),
    ]);

    return {
      departments: Array.isArray(departmentResponse?.data) ? departmentResponse.data.map(normalizeDepartment) : [],
      employees: Array.isArray(employeeResponse?.data) ? employeeResponse.data.map(normalizeReportEmployee) : [],
      statuses: TASK_STATUS_OPTIONS,
      priorities: TASK_PRIORITY_OPTIONS,
    };
  } catch {
    return {
      departments: [],
      employees: [],
      statuses: TASK_STATUS_OPTIONS,
      priorities: TASK_PRIORITY_OPTIONS,
    };
  }
};

export const fetchAllTaskReport = async (filters) => {
  const payload = {
    departmentId: normalizeId(filters?.departmentId),
    assignedEmployeeId: normalizeId(filters?.assignedEmployeeId),
    startDate: filters?.startDate || null,
    endDate: filters?.endDate || null,
    status: normalizeString(filters?.status) || null,
    priority: normalizeString(filters?.priority) || null,
  };

  try {
    const response = await post(`${config.baseurl}TaskReport/all-tasks`, payload);
    if (response?.status === 200) {
      return normalizeTaskReportResponse(response.data || {});
    }
  } catch {
    // Fall back below when the dedicated API is unavailable.
  }

  const [tasks, filterOptions] = await Promise.all([
    getAllTaskList(),
    fetchAllTaskReportFilterOptions(),
  ]);

  return applyLocalFilters(tasks, payload, filterOptions.employees);
};
